import React from 'react';
import './styles.css';
import {lessonsIcon, logoTextWhite} from '../../../../assets/images';
import {homeText} from '../../homeText';

const parseServerDateToMillis = (input?: string | Date | null): number => {
  if (!input) return NaN;
  if (input instanceof Date) return input.getTime();
  const s = input.toString();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, day] = s.split('-').map(Number);
    const localEndOfDay = new Date(y, m - 1, day, 23, 59, 59, 999);
    return localEndOfDay.getTime();
  }
  if (/T00:00:00Z$/.test(s)) {
    const dUtc = new Date(s);
    const y = dUtc.getUTCFullYear();
    const m = dUtc.getUTCMonth();
    const day = dUtc.getUTCDate();
    const localEndOfDay = new Date(y, m, day, 23, 59, 59, 999);
    return localEndOfDay.getTime();
  }

  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) {
    const t = Date.parse(s);
    return Number.isFinite(t) ? t : NaN;
  }
  const t = Date.parse(s + 'Z');
  return Number.isFinite(t) ? t : NaN;
};

const formatDueDate = (timestamp: number) =>
  new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(timestamp));

const getDueInLabel = (timestamp: number) => {
  const now = new Date();
  const due = new Date(timestamp);
  const diffDays = Math.max(
    0,
    Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)),
  );
  if (diffDays < 30) {
    return `Due in ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
  }

  let monthsLeft =
    (due.getFullYear() - now.getFullYear()) * 12 +
    (due.getMonth() - now.getMonth());
  if (due.getDate() < now.getDate()) {
    monthsLeft -= 1;
  }
  if (monthsLeft < 1) {
    return `Due in ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
  }
  return `Due in ${monthsLeft} ${monthsLeft === 1 ? 'month' : 'months'}`;
};

interface Props {
  course: any;
  onOpenCourse: () => void;
}

const CoursesCard = React.memo((props: Props) => {
  const {course} = props;
  const dueDate = course?.dueDate
    ? parseServerDateToMillis(course.dueDate)
    : NaN;
  const hasDueDate = Number.isFinite(dueDate);
  const isExpired = hasDueDate && dueDate < Date.now();
  const progressValue =
    course?.progress != null
      ? Math.min(100, Math.max(0, Number(course.progress)))
      : null;
  const dueDateLabel = isExpired
    ? homeText.expired
    : !hasDueDate
    ? homeText.noDueDate
    : getDueInLabel(dueDate);
  const dueDateTooltip = hasDueDate ? formatDueDate(dueDate) : undefined;

  return (
    <div
      className={`courses-card ${isExpired ? 'courses-card--expired' : ''}`}
      onClick={isExpired ? undefined : props.onOpenCourse}
      aria-disabled={isExpired}>
      <div className="courses-header">
        <div
          className="courses-thumb"
          style={{
            backgroundImage: course.coursePosterUrl
              ? `url(${course.coursePosterUrl})`
              : `url(${logoTextWhite})`,
            backgroundColor: course.coursePosterUrl
              ? ''
              : 'var(--primary-color-1)',
            backgroundSize: course.coursePosterUrl ? '' : 'auto',
          }}></div>
        <div className="courses-tag_container">
          <span className="courses-tag tag-math">{course.subjectTitle}</span>
        </div>
      </div>
      <h2 className="courses-title">{course.title}</h2>

      {/* <p className="courses-organization_name">{course.organizationName}</p> */}
      <p
        className="courses-due_date has-tooltip"
        data-tooltip={dueDateTooltip}
        aria-label={dueDateTooltip}>
        {dueDateLabel}
      </p>
      <div className="courses-lessons_container">
        <img src={lessonsIcon} alt="" />
        <p>
          <span>{course.amountOfLessons}</span>{' '}
          {course.amountOfLessons === 1 ? 'Lesson' : 'Lessons'}
        </p>
      </div>
      {progressValue != null ? (
        <div className="courses-progress_container">
          <div
            className="courses-progress_bar"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progressValue}>
            <div
              className="courses-progress_fill"
              style={{width: `${progressValue}%`}}
            />
          </div>
          <span className="courses-progress_meta">
            {progressValue === 100 ? 'Completed' : `Progress ${progressValue}%`}
          </span>
        </div>
      ) : null}
    </div>
  );
});

export default CoursesCard;
